<template lang="pug">
.text-field
  .form-field-label(v-if="label")
    span {{ `${label}` }}
    span.form-field-require-tag(v-show="required") *
    hint-component(v-if="hint", :text="hint")

  .form-description-area(v-if="type === 'info'")
    span(v-html="value")

  .form-field.form-editor(v-else-if="type === 'editor'", :class="invalidClass")
    q-icon.cancel-editor-field(name="cancel", v-if="value && !readonly", @click.stop="resetValue")
    vue-editor(
      ref="editor",
      v-model="value",
      :id="name || 'quill-container'",
      :editor-toolbar="customToolbar",
      :disabled="readonly",
      :placeholder="placeholder",
      @input="changeValue",
      @text-change="onTextChange"
    )

  .form-field.form-text(v-else, :class="invalidClass")
    q-input(
      filled,
      no-error-icon,
      v-model="value",
      @input="changeValue",
      :readonly="readonly",
      :placeholder="placeholder",
      :label-slot="false",
      autocomplete="off",
      type="textarea",
      input-class="text-field-area",
      :autogrow="autogrow"
    )
      q-icon.cancel-text-field(name="cancel", v-if="value && !readonly", @click.stop="resetValue")

  .valid-error-message(v-if="invalid") {{ errorMessage }}
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { Notify } from "quasar";
import { notifies } from "@/services/useLocales";
import hintComponent from "../../helpers/hint.vue";

const props = defineProps({
  name: { type: String, default: "" },
  type: { type: String, default: "text" },
  label: { type: String, default: "", required: false },
  required: { type: Boolean, default: false, required: false },
  initialValue: { type: String || null, default: null, required: false },
  placeholder: { type: String, default: "", required: false },
  readonly: { type: Boolean, default: false, required: false },
  /**
   * Параметр для проверки надобности использовать autogrow в textarea
   */
  autogrow: { type: Boolean, default: false, required: false },
  /**
   * Флаг отвечающий за валидность поля. Включает подсветку с ошибкой и текстом errorMessage
   */
  invalid: { type: Boolean, default: false, required: false },
  /**
   * Текст отображаемый при невалидном поле
   */
  errorMessage: { type: String, default: "", required: false },
  /**
   * Нужен для случаев с autogrow. Если поле будет показано уже со значением, autogrow не применится
   */
  formLoaded: { type: Boolean, default: false, required: false },
  /** Проп для вывода подсказки */
  hint: { type: String || null, default: () => null },
  /** Проп для вотча на значение select из ключа select в этом пропе и назначения значений текущего поля согласно значению select из ключа values */
  watchSelectValue: { type: Object, default: () => {} },
  /** Текущее значение селекта который мы вотчим из watchSelectValue */
  selectValueForWatch: { type: String || Number || null, default: () => null },
});

const emit = defineEmits(["change", "reset"]);

const value = ref(props.type === "editor" || props.type === "info" ? props.initialValue || "" : "");
const editor = ref(null);
const customToolbar = [["bold", "italic", "underline"]];

const formLoaded = computed(() => props.formLoaded);
const invalidClass = computed(() => (props.invalid ? "valid-error" : ""));
const selectValueForWatch = computed(() => props.selectValueForWatch);

// if autogrow input has value before show, autogrow not working and start working only after changes
onMounted(() => {
  if (props.type === "editor" || props.type === "info" || !props.autogrow || props.formLoaded) {
    value.value = props.type === "editor" ? props.initialValue || "" : props.initialValue;
  }
});

const changeValue = () => {
  emit("change", { value: value.value });
};

const onTextChange = delta => {
  if (!delta.ops.some(item => item.insert?.image)) return;

  const editorElement = editor.value.$el.querySelector(".ql-editor");
  value.value = editorElement.innerHTML.replace(/<img[^>]*>/gi, "");
  editorElement.innerHTML = value.value;
  changeValue();
  Notify.create(notifies.value.attachments_not_allowed);
};

const resetValue = () => {
  value.value = props.type === "editor" ? "" : null;
  emit("reset");
  changeValue();
};

const updateValueFromSelectWatch = val => {
  value.value = val;
  changeValue();
};

// watch for check loaded form and set initial value when form loaded and field was showed
// need only for autogrow
watch(formLoaded, (newVal, oldVal) => {
  if (newVal === true && props.autogrow) {
    value.value = props.initialValue;
  }
});

watch(
  () => props.initialValue,
  newVal => {
    value.value = props.type === "editor" ? newVal || "" : newVal;
  },
);

watch(selectValueForWatch, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    const newFieldValue = props.watchSelectValue?.values?.[newVal] ?? "";
    updateValueFromSelectWatch(newFieldValue);
  }
});
</script>

<script>
export default {
  name: "TextField",
};
</script>

<style scoped lang="scss">
.text-field {
  :deep(.text-field-area) {
    padding-right: 20px;
  }

  .form-editor {
    position: relative;
    display: block;

    :deep(.ql-editor) {
      min-height: 200px;
    }
  }
}
</style>

<style lang="scss">
@import "../../../../assets/styles/forms/fields/editor.scss";
@import "../../../../assets/styles/forms/fields/info.scss";
</style>
